import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { PortalSchema } from "../schema";
import { createDefaultSchema, validatePortalSchema } from "../schema";

interface PortalSchemaStore {
  schema: PortalSchema | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  openLoopChatPreviewEnabled: boolean;
  zendeskPreviewEnabled: boolean;
  fetchSchema: (portalV2SchemaURL?: string) => Promise<void>;
  setSchema: (schema: PortalSchema) => void;
  setOpenLoopChatPreviewEnabled: (enabled: boolean) => void;
  setZendeskPreviewEnabled: (enabled: boolean) => void;
  resetFetchState: () => void;
}

/**
 * Store for the portal schema configuration.
 *
 * This store manages the portal layout and block configuration that determines
 * what the enduser sees. It's used by both:
 * - Admin: For editing the portal configuration
 * - Enduser: For rendering the actual portal
 *
 * Schema loading priority:
 * 1. If portalV2SchemaURL is provided, fetch and validate the schema from that URL
 * 2. If fetch fails or validation fails, fall back to the default schema
 * 3. If no URL is provided, use the default schema immediately
 */
export const usePortalSchemaStore = create<PortalSchemaStore>()(
  devtools(
    (set, get) => ({
      schema: null,
      isLoading: false,
      error: null,
      hasAttemptedFetch: false,
      openLoopChatPreviewEnabled: false,
      zendeskPreviewEnabled: false,

      setSchema: (schema: PortalSchema) => {
        set({ schema });
      },

      setOpenLoopChatPreviewEnabled: (enabled: boolean) => {
        set({ openLoopChatPreviewEnabled: enabled });
      },

      setZendeskPreviewEnabled: (enabled: boolean) => {
        set({ zendeskPreviewEnabled: enabled });
      },

      resetFetchState: () => {
        set({ hasAttemptedFetch: false, schema: null, isLoading: false, error: null });
      },

      fetchSchema: async (portalV2SchemaURL?: string) => {
        // Prevent duplicate fetches
        if (get().hasAttemptedFetch || get().isLoading) {
          return;
        }

        set({ isLoading: true, error: null, hasAttemptedFetch: true });

        // If no URL provided, use default schema immediately
        if (!portalV2SchemaURL) {
          set({
            schema: createDefaultSchema(),
            isLoading: false,
            error: null,
          });
          return;
        }

        try {
          // Fetch schema from the provided URL
          const response = await fetch(portalV2SchemaURL);

          if (!response.ok) {
            throw new Error(`Failed to fetch schema: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();

          // Validate the fetched schema
          const validationResult = validatePortalSchema(data);

          if (validationResult.success && validationResult.data) {
            set({
              schema: validationResult.data,
              isLoading: false,
              error: null,
            });
          } else {
            // Validation failed - log errors and fall back to default
            console.warn("Portal schema validation failed:", validationResult.errors);
            set({
              schema: createDefaultSchema(),
              isLoading: false,
              error: `Schema validation failed: ${validationResult.errors?.join(", ")}`,
            });
          }
        } catch (error: unknown) {
          console.error("Failed to fetch portal schema:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to fetch portal schema";

          // Fall back to default schema on error
          set({
            schema: createDefaultSchema(),
            isLoading: false,
            error: errorMessage,
          });
        }
      },
    }),
    { name: "portal-schema-store" }
  )
);

// Convenience hooks
export const usePortalSchema = () => usePortalSchemaStore((state) => state.schema);
export const usePortalSchemaLoading = () => usePortalSchemaStore((state) => state.isLoading);
export const usePortalSchemaError = () => usePortalSchemaStore((state) => state.error);
export const useFetchPortalSchema = () => usePortalSchemaStore((state) => state.fetchSchema);
export const useSetPortalSchema = () => usePortalSchemaStore((state) => state.setSchema);
export const useResetPortalSchemaFetchState = () => usePortalSchemaStore((state) => state.resetFetchState);

// Settings convenience hooks
export const useLoginDescription = () => usePortalSchemaStore((state) => state.schema?.settings?.loginDescription);
export const useLoginInputLabel = () => usePortalSchemaStore((state) => state.schema?.settings?.loginInputLabel);
export const useIsLandingPageDisabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.disableLandingPage ?? false);
export const useIsRegistrationDisabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.disableRegistration ?? false);
export const useIsSMSForMFADisabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.disableSMSForMFA ?? false);

// OpenLoop Chat integration convenience hooks
export const useOpenLoopChatIntegration = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.openLoopChat);
export const useIsOpenLoopChatEnabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.openLoopChat?.enabled ?? false);
export const useOpenLoopChatPreviewEnabled = () => usePortalSchemaStore((state) => state.openLoopChatPreviewEnabled);
export const useSetOpenLoopChatPreviewEnabled = () =>
  usePortalSchemaStore((state) => state.setOpenLoopChatPreviewEnabled);

// ZenDesk integration convenience hooks
export const useZendeskIntegration = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.zendesk);
export const useIsZendeskEnabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.zendesk?.enabled ?? false);
export const useZendeskPreviewEnabled = () => usePortalSchemaStore((state) => state.zendeskPreviewEnabled);
export const useSetZendeskPreviewEnabled = () => usePortalSchemaStore((state) => state.setZendeskPreviewEnabled);

// Season integration convenience hooks
export const useSeasonRedirectTemplate = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.season?.redirectTemplate);
export const useSeasonBanner = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.integrations?.season?.banner);

// Theming convenience hooks
export const usePortalTheming = () => usePortalSchemaStore((state) => state.schema?.settings?.theming);

// Layout convenience hooks
export const usePortalLayout = () => usePortalSchemaStore((state) => state.schema?.settings?.layout);
export const useIsSidebarNavigationEnabled = () =>
  usePortalSchemaStore((state) => state.schema?.settings?.layout?.enableSidebarNavigation ?? false);
